import { useEffect, useRef, useState } from "react";
import { can } from "@/lib/rbac";
import { useT } from "@/lib/i18n";
import {
  LayoutDashboard, CalendarRange, Cpu, Users, Users2, Boxes, ShoppingCart,
  ShoppingBag, Workflow, ClipboardList, Truck, Wrench, Inbox, BellRing,
  Pin, PinOff,
} from "lucide-react";

const COLLAPSE_DELAY_MS = 300;

const Badge = ({ n, cls, testid, collapsed, idx = 0 }) => (n > 0 ? (
  <span
    className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold ${cls} ${collapsed ? "absolute" : "ml-auto"}`}
    style={collapsed ? { top: -6 + idx * 14, right: -8 } : undefined}
    data-testid={testid}
  >{n}</span>
) : null);

function NavItem({ value, icon: Icon, labelKey, active, collapsed, onSelect, testid, badges = [] }) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      data-testid={testid}
      title={collapsed ? t(labelKey) : undefined}
      className={`relative flex items-center gap-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors duration-150 ${
        collapsed ? "w-10 h-10 mx-auto justify-center p-0 overflow-visible" : "w-full px-3 py-2 overflow-hidden"
      } ${
        active
          ? "bg-blue-50 text-blue-700 shadow-[inset_2px_0_0_0_#1d4ed8]"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`}
    >
      {collapsed ? (
        <span className="relative inline-flex">
          <Icon className={`h-4 w-4 shrink-0 ${active ? "text-blue-700" : "text-slate-400"}`} />
          {badges.map((b, i) => <Badge key={i} {...b} collapsed idx={i} />)}
        </span>
      ) : (
        <>
          <Icon className={`h-4 w-4 shrink-0 ${active ? "text-blue-700" : "text-slate-400"}`} />
          <span className="truncate">{t(labelKey)}</span>
          {badges.map((b, i) => <Badge key={i} {...b} />)}
        </>
      )}
    </button>
  );
}

export function buildNavGroups(role, tabBadges, { pendingApprovals = 0, lockoutCount = 0, pendingDevices = 0, lateWorkOrders = 0 } = {}) {
  const purple = "bg-purple-600";
  const groups = [];
  const overview = [{ value: "dashboard", icon: LayoutDashboard, labelKey: "nav.dashboard" }];
  if (role === "admin") overview.push({ value: "approvals", icon: Inbox, labelKey: "nav.approvals", badges: [{ n: tabBadges.approvals, cls: purple, testid: "approvals-badge" }] });
  if (role === "planner") overview.push({
    value: "my-approvals", icon: Inbox, labelKey: "nav.myApprovals",
    badges: [
      { n: tabBadges.myPending, cls: purple, testid: "my-approvals-pending-badge" },
      { n: tabBadges.myRejected, cls: "bg-rose-600", testid: "my-approvals-rejected-badge" },
    ],
  });
  if (role === "operator") overview.push({ value: "updates", icon: BellRing, labelKey: "nav.updates", badges: [{ n: tabBadges.updates, cls: "bg-blue-600", testid: "updates-badge" }] });
  groups.push({ key: "overview", items: overview });

  const operations = [
    { value: "work-orders", icon: Wrench, labelKey: "nav.workOrders", badges: [{ n: lateWorkOrders, cls: "bg-red-600", testid: "work-orders-late-badge" }] },
    { value: "schedule", icon: CalendarRange, labelKey: "nav.schedule" },
    { value: "machines", icon: Cpu, labelKey: "nav.lines" },
  ];
  if (can.manageOrders(role)) operations.push({ value: "system", icon: Workflow, labelKey: "nav.systemMap" });
  groups.push({ key: "operations", items: operations });

  const manufacturing = [
    { value: "bom", icon: Boxes, labelKey: "nav.bom", badges: role === "admin" ? [{ n: tabBadges.bom, cls: purple, testid: "bom-approval-badge" }] : [] },
  ];
  if (can.manageOrders(role)) {
    manufacturing.push(
      { value: "inventory", icon: Boxes, labelKey: "nav.inventory" },
      { value: "planning", icon: ClipboardList, labelKey: "nav.planning" },
    );
  }
  groups.push({ key: "manufacturing", items: manufacturing });

  const supplyChain = [{
    value: "purchasing", icon: ShoppingCart, labelKey: "nav.purchasing",
    badges: role === "admin" ? [{ n: pendingApprovals, cls: purple, testid: "awaiting-approval-badge" }] : [],
  }];
  if (can.manageOrders(role)) supplyChain.push({ value: "osp", icon: Truck, labelKey: "nav.osp" });
  groups.push({ key: "supplyChain", items: supplyChain });

  if (can.manageOrders(role)) {
    groups.push({
      key: "partners",
      items: [
        { value: "suppliers", icon: Users2, labelKey: "nav.suppliers", badges: role === "admin" ? [{ n: tabBadges.suppliers, cls: purple, testid: "suppliers-approval-badge" }] : [] },
        { value: "customers", icon: Users2, labelKey: "nav.customers", badges: role === "admin" ? [{ n: tabBadges.customers, cls: purple, testid: "customers-approval-badge" }] : [] },
        { value: "sales", icon: ShoppingBag, labelKey: "nav.sales", badges: role === "admin" ? [{ n: tabBadges.sales, cls: purple, testid: "sales-approval-badge" }] : [] },
      ],
    });
  }

  if (can.viewUsers(role)) {
    groups.push({
      key: "admin",
      items: [{
        value: "users", icon: Users, labelKey: "nav.users",
        badges: [
          { n: lockoutCount, cls: "bg-red-600", testid: "users-lockout-badge" },
          { n: pendingDevices, cls: "bg-amber-500", testid: "users-device-badge" },
        ],
      }],
    });
  }
  return groups;
}

export default function AppSidebar({ role, tab, onTab, pinned = false, onTogglePinned, tabBadges = {}, counts = {}, mobile = false }) {
  const t = useT();
  const groups = buildNavGroups(role, tabBadges, counts);
  // Starts expanded on first load; collapses to the icon rail 300ms after the cursor leaves.
  const [hovered, setHovered] = useState(true);
  const leaveTimer = useRef(null);
  useEffect(() => () => clearTimeout(leaveTimer.current), []);
  const isCollapsed = !mobile && !pinned && !hovered;

  const onEnter = () => {
    clearTimeout(leaveTimer.current);
    leaveTimer.current = null;
    setHovered(true);
  };
  const onLeave = () => {
    if (pinned) return;
    clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => setHovered(false), COLLAPSE_DELAY_MS);
  };

  return (
    <aside
      data-testid="app-sidebar"
      data-collapsed={isCollapsed}
      data-pinned={pinned}
      onMouseEnter={mobile ? undefined : onEnter}
      onMouseLeave={mobile ? undefined : onLeave}
      className={`${mobile ? "w-full" : `${isCollapsed ? "w-16" : "w-60"} hidden lg:flex sticky top-[var(--nav-h)] h-[calc(100vh_-_var(--nav-h))] shrink-0 transition-[width] duration-300 ease-in-out`} flex-col bg-white border-r border-slate-200 overflow-hidden`}
    >
      {!mobile && (
        <div className={`flex items-center border-b border-slate-100 py-1.5 ${isCollapsed ? "justify-center px-0" : "justify-end px-3"}`}>
          <button
            type="button"
            onClick={onTogglePinned}
            data-testid="sidebar-pin"
            aria-pressed={pinned}
            title={pinned ? t("nav.unpinSidebar") : t("nav.pinSidebar")}
            className={`rounded-md p-1.5 transition-colors ${pinned ? "text-blue-600 bg-blue-50 hover:bg-blue-100" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"}`}
          >
            {pinned ? <Pin className="h-4 w-4" /> : <PinOff className="h-4 w-4" />}
          </button>
        </div>
      )}
      <nav className={`flex-1 overflow-y-auto py-4 space-y-5 ${isCollapsed ? "px-0" : "px-3"}`} data-testid="sidebar-nav">
        {groups.map((g) => (
          <div key={g.key} data-testid={`sidebar-group-${g.key}`}>
            {!isCollapsed && (
              <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400 whitespace-nowrap">{t(`nav.group.${g.key}`)}</p>
            )}
            <div className={isCollapsed ? "space-y-2.5" : "space-y-0.5"}>
              {g.items.map((item) => (
                <NavItem
                  key={item.value}
                  {...item}
                  active={tab === item.value}
                  collapsed={isCollapsed}
                  onSelect={onTab}
                  testid={`tab-${item.value}`}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}
